/**
 * Mux API Integration
 * Fetches videos from Mux API filtered by metadata via server-side API endpoint
 *
 * Features:
 * - Multi-layer caching: localStorage (persistent) + in-memory (fast)
 * - localStorage cache survives page reloads for same user
 * - Automatic cache invalidation after TTL
 * - Supports flexible metadata structure (can be in metadata object or passthrough)
 *
 * creator_id is read from brandConfig (VITE_BRAND_MUX_CREATOR_ID / VITE_BRAND_SOURCE_KEY)
 * so this works for any brand without code changes.
 */

import { BRAND_MUX_CREATOR_ID } from "@/lib/brandConfig";

interface MuxApiResponse {
  playbackIds: Array<string | { id?: string }>;
  error?: string;
}

function normalizePlaybackIds(raw: MuxApiResponse["playbackIds"] | undefined): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    const id =
      typeof item === "string"
        ? item.trim()
        : typeof item?.id === "string"
          ? item.id.trim()
          : "";
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

// In-memory cache with TTL (fast, but lost on page reload)
interface CacheEntry {
  data: string[];
  timestamp: number;
}

const memoryCache = new Map<string, CacheEntry>();
const MEMORY_CACHE_TTL = 5 * 60 * 1000; // 5 minutes (fast access)
const LOCALSTORAGE_CACHE_TTL = 60 * 60 * 1000; // 1 hour (persistent across reloads)
const LOCALSTORAGE_PREFIX = "mux_cache_";
const pendingRequests = new Map<string, Promise<string[]>>();

/**
 * Get data from localStorage cache
 */
function getFromLocalStorage(cacheKey: string): string[] | null {
  if (typeof window === "undefined" || !window.localStorage) {
    return null;
  }
  
  try {
    const stored = localStorage.getItem(LOCALSTORAGE_PREFIX + cacheKey);
    if (!stored) return null;
    
    const parsed = JSON.parse(stored) as CacheEntry;
    
    // Check if cache is still valid
    if (Date.now() - parsed.timestamp < LOCALSTORAGE_CACHE_TTL) {
      return parsed.data;
    }
    
    // Cache expired, remove it
    localStorage.removeItem(LOCALSTORAGE_PREFIX + cacheKey);
    return null;
  } catch (error) {
    console.warn("[muxApi] Failed to read from localStorage:", error);
    return null;
  }
}

/**
 * Save data to localStorage cache
 */
function saveToLocalStorage(cacheKey: string, data: string[]): void {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }
  
  try {
    const entry: CacheEntry = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(LOCALSTORAGE_PREFIX + cacheKey, JSON.stringify(entry));
  } catch (error) {
    console.warn("[muxApi] Failed to save to localStorage:", error);
  }
}

/**
 * Clear all Mux caches (useful for manual invalidation)
 */
export function clearMuxCache(): void {
  // Clear memory cache
  memoryCache.clear();
  
  // Clear localStorage cache
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(LOCALSTORAGE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
      console.log("[muxApi] All Mux caches cleared");
    } catch (error) {
      console.warn("[muxApi] Failed to clear localStorage cache:", error);
    }
  }
}

/**
 * Get Mux videos filtered by metadata
 * Uses multi-layer caching: localStorage (persistent) + memory (fast) + deduplication
 */
export async function getMuxVideosWithMetadata(
  externalId: string = "feed",
  creatorId: string = BRAND_MUX_CREATOR_ID
): Promise<string[]> {
  const cacheKey = `${externalId}:${creatorId}`;
  
  // Layer 1: Check in-memory cache (fastest)
  const memCached = memoryCache.get(cacheKey);
  if (memCached && Date.now() - memCached.timestamp < MEMORY_CACHE_TTL) {
    console.log(`[muxApi] Memory cache hit for ${cacheKey}`);
    return memCached.data;
  }
  
  // Layer 2: Check localStorage cache (survives page reloads)
  const localStorageCached = getFromLocalStorage(cacheKey);
  if (localStorageCached) {
    console.log(`[muxApi] localStorage cache hit for ${cacheKey}`);
    // Also populate memory cache for faster subsequent access
    memoryCache.set(cacheKey, { data: localStorageCached, timestamp: Date.now() });
    return localStorageCached;
  }
  
  // Layer 3: Check if there's already a pending request for this key (deduplication)
  const pending = pendingRequests.get(cacheKey);
  if (pending) {
    console.log(`[muxApi] Reusing pending request for ${cacheKey}`);
    return pending;
  }
  
  // Layer 4: Fetch from API
  const fetchPromise = (async () => {
    try {
      console.log(`[muxApi] Fetching from API for ${cacheKey}`);
      const apiUrl = `/api/mux-videos?external_id=${encodeURIComponent(externalId)}&creator_id=${encodeURIComponent(creatorId)}`;
      
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        console.warn(`[muxApi] API request failed with status ${response.status}`);
        return [];
      }

      const data: MuxApiResponse = await response.json();
      const playbackIds = normalizePlaybackIds(data.playbackIds);
      
      console.log(`[muxApi] API returned ${playbackIds.length} playback IDs for ${cacheKey}`);
      
      // Store in both caches
      memoryCache.set(cacheKey, { data: playbackIds, timestamp: Date.now() });
      saveToLocalStorage(cacheKey, playbackIds);
      
      return playbackIds;
    } catch (error) {
      console.error(`[muxApi] Error fetching ${cacheKey}:`, error);
      return [];
    } finally {
      // Remove from pending
      pendingRequests.delete(cacheKey);
    }
  })();
  
  // Store as pending
  pendingRequests.set(cacheKey, fetchPromise);
  
  return fetchPromise;
}

/**
 * Get playback IDs for BaseTemplate gallery.
 * BaseTemplate usa siempre y solo: external_id "feed" y creator_id from brandConfig.
 */
export async function getBaseTemplateMuxPlaybackIds(): Promise<string[]> {
  const playbackIds = await getMuxVideosWithMetadata("feed", BRAND_MUX_CREATOR_ID);
  return playbackIds;
}

/**
 * Get playback IDs for Reels page (`/reels`).
 * Canonical source (2026-07): same pool as BaseTemplate —
 * external_id "feed" + brand creator_id (BRAND_SOURCE_KEY).
 * Curated lists are applied via playbackIdsPreset from admin config.
 */
export async function getReelsMuxPlaybackIds(): Promise<string[]> {
  const playbackIds = await getMuxVideosWithMetadata("feed", BRAND_MUX_CREATOR_ID);
  return playbackIds;
}

/**
 * External ID + title que el equipo usa en Mux para el teaser del DualCTAVideoLanding.
 *
 * Convención del brand delfuocampo (confirmada por el admin 2026-06-14):
 *   creator_id = "delfuocampo"
 *   external_id = "dlfcpo"
 *
 * Historia: el 2026-06-13 alguien cambió este value a "teaser" creyendo que
 * el admin iba a re-taggear, pero los assets en Mux quedaron con "dlfcpo".
 * Resultado: el filter devolvía [] y el frontend caía al hardcoded viejo
 * "pu5Hkn..." aunque la env Vercel tuviera el ID correcto. Revertido al
 * value real con el que el operador tagea.
 *
 * Si el operador cambia la convención en Mux en el futuro, este es el único
 * lugar a tocar — no hay literales en otros archivos (limpieza MR !190).
 */
export const DUAL_CTA_TEASER_EXTERNAL_ID = "dlfcpo";
export const DUAL_CTA_TEASER_TITLE = "teaserdelfu";

/**
 * Get playback IDs for the DualCTAVideoLanding teaser.
 * Tagged in Mux as creator_id=delfuocampo, external_id="teaser".
 * Empty array if no ready asset matches yet (caller keeps its fallback chain).
 */
export async function getDualCtaTeaserMuxPlaybackIds(): Promise<string[]> {
  return getMuxVideosWithMetadata(DUAL_CTA_TEASER_EXTERNAL_ID, BRAND_MUX_CREATOR_ID);
}
